import React, {useState} from 'react';

import {NavigationProps, RouteProps} from 'config/navigation';
import {useLocation, useNavigate} from 'react-router-dom';
import useAccessControls from 'modules/access-controls/atoms/use-access-controls';
import {Collapse} from '@mantine/core';

const DrawerOption: React.FC<NavigationProps> = ({
  name,
  icon,
  routes,
  subRoutes,
  path,
}) => {
  const {pathname} = useLocation();
  const [opened, setOpen] = useState(pathname.includes(path));
  const {isAllowed, isModuleAllowed, companyId, cityId} = useAccessControls();
  const [module, resource] = pathname.split('/').filter((pathname) => pathname);
  const navigate = useNavigate();

  const isNavigationAllowed = isModuleAllowed(
    routes
      ?.filter((route) => !route.hidden)
      .map(({permission, exactPermission}) =>
        exactPermission ? {name: permission, exactPermission} : permission,
      ),
  );

  const getDefaultPath = (route: RouteProps) => {
    if (route.addCompanyUrl) return `/${companyId}`;
    if (route.addCityUrl) return `/${cityId}`;
    return '';
  };

  return (
    <Container>
      {subRoutes && (
        <MainLink to={`/${path}`}>
          <MainTitle>
            {icon}
            {name}
          </MainTitle>
        </MainLink>
      )}
      {routes && isNavigationAllowed && (
        <MainButton onClick={() => setOpen(!opened)} {...{opened}}>
          {icon}
          <MainTitle>{name}</MainTitle>
        </MainButton>
      )}
      {!routes && isNavigationAllowed && (
        <LinkButton to={`/${path}`}>
          <Label>
            {icon}
            {name}
          </Label>
        </LinkButton>
      )}
      {routes && (
        <Collapse in={opened}>
          {routes
            .filter((route) => !route.hidden)
            .filter(
              (route) =>
                (!route.addCityUrl && !route.addCompanyUrl) ||
                (route.addCityUrl && cityId) ||
                (route.addCompanyUrl && companyId),
            )
            ?.sort((a, b) => a.name.localeCompare(b.name))
            .map((route, index) =>
              isAllowed(route.permission, route.exactPermission) ? (
                <Row
                  key={index}
                  onClick={() =>
                    navigate(
                      route.redirectUrl
                        ? ''
                        : `/${path}/${route.path}${getDefaultPath(route)}`,
                    )
                  }>
                  <RouteName
                    active={
                      path === module &&
                      (route.path === resource ? true : false)
                    }
                    onClick={() =>
                      route.redirectUrl && window.open(route.redirectUrl)
                    }>
                    {route.name}
                  </RouteName>
                </Row>
              ) : null,
            )}
        </Collapse>
      )}
    </Container>
  );
};

export default DrawerOption;

import styled from 'styled-components';
import {Link} from 'react-router-dom';

interface OptionProps {
  active?: number;
}

const Container = styled.div`
  .collapse-css-transition {
    transition: height 280ms cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

const LinkButton = styled(Link)<ButtonProps>`
  display: flex;
  align-items: center;
  padding: 15px 30px;
  transition: all 0.1s ease-in-out;
  cursor: pointer;

  > svg {
    transition: all 280ms ease-in-out;
    transform: ${(props) => props.opened && 'rotate(-180deg)'};
  }

  &:hover {
    background-color: ${(props) => props.theme.colors.primary};
  }
`;

const Label = styled.div<OptionProps>`
  border-radius: 5px;
  display: flex;
  flex: 1;
  gap: 10px;
  align-items: center;
  color: ${(props) => props.theme.colors.text};
  font-weight: 600;
  font-size: 12px;

  background-color: ${(props) =>
    props.active ? props.theme.colors.primaryLight : props.theme.colors.card};
`;

const Row = styled.div`
  gap: 10px;
  display: flex;
  align-items: center;
  padding: 10px 15px;
  border-radius: 8px;
  cursor: pointer;
`;

interface RouteNameProps {
  active: boolean;
}

const RouteName = styled.p<RouteNameProps>`
  color: ${(props) =>
    props.active ? props.theme.colors.primary : props.theme.colors.text};
  font-weight: 400;
`;

interface ButtonProps {
  opened?: boolean;
}

const MainButton = styled.div<ButtonProps>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border-radius: 10px;
  cursor: pointer;
  background-color: ${(props) =>
    props.opened ? props.theme.colors.primaryLight : props.theme.colors.card};

  svg {
    font-size: 18px;
  }

  h3,
  svg {
    color: ${(props) =>
      props.opened ? props.theme.colors.primary : props.theme.colors.text};
  }

  &:hover {
    background-color: ${(props) => props.theme.colors.primaryLight};
    h3,
    svg {
      color: ${(props) => props.theme.colors.primary};
    }
  }
`;

const MainLink = styled(Link)<ButtonProps>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border-radius: 10px;
  cursor: pointer;
  background-color: ${(props) =>
    props.opened ? props.theme.colors.primaryLight : props.theme.colors.card};

  svg {
    font-size: 18px;
  }

  h3,
  svg {
    color: ${(props) =>
      props.opened ? props.theme.colors.primary : props.theme.colors.text};
  }

  &:hover {
    background-color: ${(props) => props.theme.colors.primaryLight};
    h3,
    svg {
      color: ${(props) => props.theme.colors.primary};
    }
  }
`;

const MainTitle = styled.h3<OptionProps>`
  border-radius: 5px;
  display: flex;
  flex: 1;
  gap: 10px;
  align-items: center;
  font-weight: 500;
  font-size: 12px;
`;
